import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import AuthLayout, { AuthHeading } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export const Route = createFileRoute("/auth/login-2fa")({
  component: Login2FAPage,
});

function Login2FAPage() {
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const { verify2FA, user } = useAuthStore();
  const navigate = useNavigate();

  const handleVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (code.length < 6) return;

    setLoading(true);
    try {
      const role = await verify2FA(code, trustDevice);
      let path =
        role === "student"
          ? "/student/dashboard"
          : role === "partner"
            ? "/partner/dashboard"
            : "/admin/dashboard";
      if (role === "unassigned") path = "/onboarding";
      navigate({ to: path as any });
    } catch (err: any) {
      toast.error("Verification failed", {
        description: err?.response?.data?.message || "Invalid or expired code",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthHeading
        title="Verify your identity"
        description={
          <>
            We've sent a 6-digit code to <span className="text-foreground">{user?.email}</span>.
            Enter it below to continue.
          </>
        }
      />

      <form onSubmit={handleVerify} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="otp">Verification code</Label>
          <InputOTP
            id="otp"
            maxLength={6}
            value={code}
            onChange={setCode}
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            containerClassName="justify-between"
          >
            <InputOTPGroup className="w-full justify-between gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="trust-device"
            checked={trustDevice}
            onCheckedChange={(v) => setTrustDevice(v === true)}
          />
          <Label htmlFor="trust-device" className="font-normal text-body">
            Trust this browser for 30 days
          </Label>
        </div>

        <Button
          type="submit"
          className="w-full"
          size="lg"
          loading={loading}
          disabled={code.length < 6}
        >
          Verify and sign in
        </Button>
      </form>

      <Button
        variant="ghost"
        size="sm"
        className="mt-4 w-full text-muted-foreground"
        onClick={() => navigate({ to: "/auth/login" as any })}
      >
        <ArrowLeft />
        Back to sign in
      </Button>
    </AuthLayout>
  );
}

export default Login2FAPage;
